import {
  useEffect,
  useState,
  useRef,
  ChangeEvent,
} from "react"
import { useParams } from "react-router-dom"

import { APP_CONFIG } from "../../shared/config/app.config"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  ChatMessage,
  listChatMessagesApi,
  getOrCreateChatApi,
  uploadChatImageApi,
  uploadChatFileApi,
} from "../../features/chat/api/chat.api"
import { useCourseLessonMap, LessonMapEntry } from "../../features/chat/hooks/useCourseLessonMap"
import { LessonFilter } from "../../features/chat/ui/LessonFilterBar"
import { LessonSidebar } from "../../features/chat/ui/LessonSidebar"
import { LessonBadge } from "../../features/chat/ui/LessonBadge"
import { LessonPreviewDrawer } from "../../features/chat/ui/LessonPreviewDrawer"
import { Avatar } from "../../shared/ui/Avatar"

/* ===================== CONST ===================== */

const LIMIT = 20
const FILTERED_LIMIT = 200

/* ===================== PAGE ===================== */

export const CoursePrivateChatHistoryPage = () => {
  /* ===================== PARAMS ===================== */

  const { courseId, studentId } = useParams<{
    courseId?: string
    studentId?: string
  }>()

  /* ===================== REFS ===================== */

  const listRef = useRef<HTMLDivElement>(null)
  const socketRef = useRef<WebSocket | null>(null)

  /* ===================== STATE ===================== */

  const [chatId, setChatId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const [text, setText] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const [previews, setPreviews] = useState<string[]>([])
  const [sending, setSending] = useState(false)

  /* ===================== LESSON / MODULE CONTEXT ===================== */

  const [filter, setFilter] = useState<LessonFilter>({ type: "all" })
  const [filterLoading, setFilterLoading] = useState(false)
  const [previewLessonId, setPreviewLessonId] = useState<string | null>(null)
  const { modules, lessonMap } = useCourseLessonMap(courseId)

  // Read from refs inside the WS handler so we don't have to reconnect the socket on every filter change.
  const filterRef = useRef(filter)
  useEffect(() => { filterRef.current = filter }, [filter])
  const lessonMapRef = useRef(lessonMap)
  useEffect(() => { lessonMapRef.current = lessonMap }, [lessonMap])

  /* ===================== AUTH ===================== */

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("access_token")
      : null

  const currentUserId =
    typeof window !== "undefined"
      ? localStorage.getItem("user_id")
      : null

  /* ===================== GET OR CREATE CHAT ===================== */

  useEffect(() => {
    if (!courseId || !studentId) return

    const init = async () => {
      setLoading(true)
      try {
        const res = await getOrCreateChatApi({
          chat_type: "course_private",
          course_id: courseId,
          student_id: studentId,
        })
        setChatId(res.data.chat_id)
      } finally {
        setLoading(false)
      }
    }

    init()
  }, [courseId, studentId])

  /* ===================== LOAD HISTORY (unfiltered, paginated) ===================== */

  const loadMessages = async () => {
    if (!chatId) return

    const container = listRef.current
    const prevHeight = container?.scrollHeight ?? 0

    const res = await listChatMessagesApi(
      chatId,
      LIMIT,
      offset
    )

    setMessages((prev) => {
      const ids = new Set(prev.map((m) => m.id))
      const unique = res.data.messages.filter(
        (m) => !ids.has(m.id)
      )
      return [...unique, ...prev]
    })

    setTotal(res.data.total)
    setOffset((p) => p + LIMIT)

    requestAnimationFrame(() => {
      if (!container) return
      container.scrollTop =
        container.scrollHeight - prevHeight
    })
  }

  useEffect(() => {
    if (!chatId || filter.type !== "all") return
    setMessages([])
    setOffset(0)
    setTotal(0)
    loadMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, filter.type === "all"])

  /* ===================== LOAD HISTORY (by lesson / module) ===================== */

  useEffect(() => {
    if (!chatId || filter.type === "all") return

    let cancelled = false
    setFilterLoading(true)

    ;(async () => {
      try {
        if (filter.type === "lesson") {
          const res = await listChatMessagesApi(chatId, FILTERED_LIMIT, 0, filter.id)
          // Re-filter on the client too — a safety net in case the backend's lesson_id
          // param isn't actually narrowing the result yet.
          const filtered = res.data.messages.filter(
            (m) => m.context?.reference?.type === "lesson" && m.context.reference.id === filter.id
          )
          if (!cancelled) {
            setMessages(filtered)
            setTotal(filtered.length)
          }
        } else {
          const res = await listChatMessagesApi(chatId, FILTERED_LIMIT, 0)
          if (!cancelled) {
            const lessonIds = new Set(
              modules.find((m) => m.moduleId === filter.id)?.lessons.map((l) => l.id) ?? []
            )
            const filtered = res.data.messages.filter(
              (m) => m.context?.reference?.type === "lesson" && lessonIds.has(m.context.reference.id)
            )
            setMessages(filtered)
            setTotal(filtered.length)
          }
        }
      } catch {
        if (!cancelled) { setMessages([]); setTotal(0) }
      } finally {
        if (!cancelled) setFilterLoading(false)
      }
    })()

    return () => { cancelled = true }
  }, [chatId, filter, modules])

  const hasMore = filter.type === "all" && messages.length < total

  /* ===================== WEBSOCKET ===================== */

  useEffect(() => {
    if (!chatId || !token) return

    const ws = new WebSocket(
      APP_CONFIG.WS_URL+`/api/ws/chats?token=${token}`
    )

    socketRef.current = ws

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data)
      if (data.event !== "new_message") return

      const msg: ChatMessage = data.message

      const f = filterRef.current
      const matches =
        f.type === "all" ||
        (f.type === "lesson" && msg.context?.reference?.type === "lesson" && msg.context.reference.id === f.id) ||
        (f.type === "module" && msg.context?.reference?.type === "lesson" && lessonMapRef.current[msg.context.reference.id]?.moduleId === f.id)

      if (!matches) return

      setMessages((prev) =>
        prev.some((m) => m.id === msg.id)
          ? prev
          : [...prev, msg]
      )

      requestAnimationFrame(() => {
        const el = listRef.current
        if (el) el.scrollTop = el.scrollHeight
      })
    }

    return () => ws.close()
  }, [chatId, token])

  /* ===================== FILE PICK ===================== */

  const onPickFiles = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return

    const selected = Array.from(e.target.files)
    setFiles(selected)

    const imgs = selected
      .filter((f) => f.type.startsWith("image/"))
      .map((f) => URL.createObjectURL(f))

    setPreviews(imgs)
  }

  /* ===================== UPLOAD ===================== */

  const uploadAttachments = async () => {
    const attachments: {
      file_id: string
      url: string
    }[] = []

    let type: "text" | "image" | "file" = "text"

    for (const file of files) {
      if (file.type.startsWith("image/")) {
        const res = await uploadChatImageApi(file)
        attachments.push({
          file_id: res.data.id,
          url: res.data.url,
        })
        type = "image"
      } else {
        const res = await uploadChatFileApi(file)
        attachments.push({
          file_id: res.data.file_id,
          url: res.data.url,
        })
        type = "file"
      }
    }

    return { attachments, type }
  }

  /* ===================== SEND ===================== */

  const onSend = async () => {
    if (!socketRef.current) return
    if (!text.trim() && files.length === 0) return
    if (sending) return

    setSending(true)

    let attachments: any[] = []
    let type: "text" | "image" | "file" = "text"

    try {
      if (files.length > 0) {
        const uploaded = await uploadAttachments()
        attachments = uploaded.attachments
        type = uploaded.type
      }

      const outgoing: Record<string, unknown> = {
        action: "send_message",
        chat_type: "course_private",
        course_id: courseId,
        student_id: studentId,
        payload: {
          type,
          text,
          attachments,
        },
      }

      // Replying while a lesson filter is active keeps the thread tied to that lesson.
      if (filter.type === "lesson") {
        outgoing.context = { reference: { type: "lesson", id: filter.id } }
      }

      socketRef.current.send(JSON.stringify(outgoing))

      previews.forEach((u) => URL.revokeObjectURL(u))
      setText("")
      setFiles([])
      setPreviews([])
    } finally {
      setSending(false)
    }
  }

  /* ===================== RENDER ===================== */

  if (!courseId || !studentId) {
    return (
      <DashboardLayout title="Личный чат">
        <div className="p-6 text-gray-400">
          Некорректные параметры чата
        </div>
      </DashboardLayout>
    )
  }

  if (loading || !chatId) {
    return (
      <DashboardLayout title="Личный чат">
        <div className="p-6 text-gray-400">
          Открываем личный чат…
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title="Личный чат">
      <div className="max-w-5xl mx-auto flex gap-4 h-[calc(100vh-120px)]">

        {modules.length > 0 && (
          <div className="w-60 flex-shrink-0 bg-white border rounded-2xl overflow-y-auto p-2.5">
            <LessonSidebar modules={modules} value={filter} onChange={setFilter} />
          </div>
        )}

        <div className="flex-1 min-w-0 bg-white border rounded-2xl flex flex-col">

          {filter.type === "lesson" && (
            <div className="px-4 py-2 border-b bg-amber-50/60 text-[11px] text-amber-700 flex-shrink-0">
              Ответ будет привязан к этому уроку
            </div>
          )}

          {hasMore && (
            <div className="p-2 text-center">
              <button
                onClick={loadMessages}
                className="text-sm text-blue-600 hover:underline"
              >
                Загрузить ещё
              </button>
            </div>
          )}

          <div
            ref={listRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
          >
            {filterLoading ? (
              <div className="flex justify-center py-10">
                <span className="w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 && filter.type !== "all" ? (
              <p className="text-sm text-gray-400 text-center py-10">
                Сообщений по {filter.type === "lesson" ? "этому уроку" : "этому модулю"} пока нет
              </p>
            ) : (
              messages.map((m, i) => {
                const prev = messages[i - 1]
                const isMine = m.sender_id === currentUserId
                const showHeader =
                  !isMine &&
                  (!prev || prev.sender_id !== m.sender_id)

                return (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    isMine={isMine}
                    showHeader={showHeader}
                    lessonMap={lessonMap}
                    onSelectLesson={(lessonId) => setPreviewLessonId(lessonId)}
                  />
                )
              })
            )}
          </div>

          {previews.length > 0 && (
            <div className="flex gap-2 px-3 pb-2 overflow-x-auto">
              {previews.map((src) => (
                <img
                  key={src}
                  src={src}
                  className="w-20 h-20 rounded-lg object-cover border"
                />
              ))}
            </div>
          )}

          <div className="border-t p-3 flex gap-2 items-end">
            <label className="cursor-pointer px-3 py-2 border rounded-lg">
              📎
              <input
                type="file"
                multiple
                hidden
                onChange={onPickFiles}
              />
            </label>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={1}
              placeholder="Личное сообщение…"
              className="flex-1 resize-none border rounded-lg px-3 py-2 text-sm"
            />

            <button
              onClick={onSend}
              disabled={sending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
            >
              Отправить
            </button>
          </div>
        </div>
      </div>

      <LessonPreviewDrawer
        lessonId={previewLessonId}
        lessonMap={lessonMap}
        onClose={() => setPreviewLessonId(null)}
        onFilter={(id) => { setFilter({ type: "lesson", id }); setPreviewLessonId(null) }}
      />
    </DashboardLayout>
  )
}

const MessageBubble = ({
  message,
  isMine,
  showHeader,
  lessonMap,
  onSelectLesson,
}: {
  message: ChatMessage
  isMine: boolean
  showHeader: boolean
  lessonMap: Record<string, LessonMapEntry>
  onSelectLesson: (lessonId: string) => void
}) => {
  const lessonRef = message.context?.reference?.type === "lesson" ? message.context.reference : null

  return (
    <div
      className={`flex ${
        isMine ? "justify-end" : "justify-start"
      }`}
    >
      <div className="max-w-[300px]">
        {showHeader && message.sender && (
          <div className="flex items-center gap-2 mb-1">
            <Avatar
              name={message.sender.full_name}
              src={message.sender.avatar ? normalizeUrl(message.sender.avatar) : null}
              size={28}
            />
            <div className="text-xs font-medium text-gray-600">
              {message.sender.full_name || "Пользователь"}
            </div>
          </div>
        )}

        {lessonRef && (
          <div className={isMine ? "flex justify-end" : ""}>
            <LessonBadge
              title={lessonMap[lessonRef.id]?.title ?? "Урок"}
              onClick={() => onSelectLesson(lessonRef.id)}
            />
          </div>
        )}

        {/* IMAGE */}
        {message.type === "image" &&
          message.attachments.map((a) => (
            <img
              key={a.file_id}
              src={normalizeUrl(a.url)}
              className="rounded-xl mb-1 max-w-full"
            />
          ))}

        {/* FILE */}
        {message.type === "file" &&
          message.attachments.map((a) => (
            <a
              key={a.file_id}
              href={normalizeUrl(a.url)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 p-3 border rounded-xl hover:bg-gray-50 transition mb-1"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 text-lg">
                📄
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {a.filename || "Файл"}
                </div>
                {a.size && (
                  <div className="text-xs text-gray-400">
                    {formatSize(a.size)}
                  </div>
                )}
              </div>
            </a>
          ))}

        {/* TEXT */}
        {message.text && (
          <div
            className={`px-4 py-2 rounded-xl text-sm ${
              isMine
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-900"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="text-[10px] text-gray-400 text-right mt-1">
          {formatTime(message.created_at)}
        </div>
      </div>
    </div>
  )
}

/* ===================== HELPERS ===================== */

const normalizeUrl = (url: string) =>
  url.startsWith("http") ? url : `https://${url}`

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024)
    return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
